import * as QRCodeLibrary from 'qrcode';

// A4 landscape dimensions in points
const PAGE_WIDTH = 842;
const PAGE_HEIGHT = 595;
const MARGIN = 40;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;
const CONTENT_HEIGHT = PAGE_HEIGHT - 2 * MARGIN;
const BORDER_RADIUS = 4;

const HEADER_HEIGHT = 65;
const HEADER_DIVIDER_Y = MARGIN + HEADER_HEIGHT;

const COL_PADDING = 15;
const LEFT_COL_X = MARGIN + COL_PADDING;
const COL_DIVIDER_X = MARGIN + CONTENT_WIDTH / 2;
const RIGHT_COL_X = COL_DIVIDER_X + COL_PADDING;
const COL_WIDTH = CONTENT_WIDTH / 2 - 2 * COL_PADDING;
const CONTENT_TOP_Y = HEADER_DIVIDER_Y + COL_PADDING;

const TN_COLOR = '#1a5276';
const EVAL_COLOR = '#8e24aa';
const TEXT_PRIMARY = '#333333';
const TEXT_SECONDARY = '#666666';

interface AppointmentInfo {
  _id: string;
  start?: Date | null;
  end?: Date | null;
  location?: string | null;
  name?: string | null;
}

interface ConfirmationLinkInfo {
  appointmentID: string;
  url: string;
  validFrom?: Date | null;
  validTill?: Date | null;
}

export interface TnQrCodePdfParams {
  workshopName: string;
  courseLocation: string;
  appointments: AppointmentInfo[];
  confirmationLinks: ConfirmationLinkInfo[];
  logoBuffer?: Buffer | null;
}

export interface EvaluationQrCodePdfParams {
  workshopName: string;
  courseLocation: string;
  appointments: AppointmentInfo[];
  evaluationLink: string;
  evaluationCode: string;
  logoBuffer?: Buffer | null;
}

export async function generateQrCodePng(url: string, size = 300): Promise<string> {
  return QRCodeLibrary.toDataURL(url, { width: size, margin: 1 });
}


const TZ = 'Europe/Berlin';

function formatDateTime(date: Date): string {
  return date.toLocaleString('de-DE', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDate(date: Date): string {
  return date.toLocaleString('de-DE', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatTime(date: Date): string {
  return date.toLocaleString('de-DE', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
}

function getAppointmentDateString(appointments: AppointmentInfo[]): string {
  return appointments
    .filter(a => a.start)
    .map(a => {
      const start = a.start!;
      const end = a.end;
      const dateStr = formatDate(start);
      const startTime = formatTime(start);
      if (end) return `${dateStr} ${startTime} \u2013 ${formatTime(end)}`;
      return `${dateStr} ${startTime}`;
    })
    .join(', ');
}

function drawPageFrame(doc: any, borderColor: string) {
  doc
    .roundedRect(MARGIN, MARGIN, CONTENT_WIDTH, CONTENT_HEIGHT, BORDER_RADIUS)
    .lineWidth(2)
    .strokeColor(borderColor)
    .stroke();

  doc.moveTo(MARGIN, HEADER_DIVIDER_Y).lineTo(MARGIN + CONTENT_WIDTH, HEADER_DIVIDER_Y).lineWidth(1).stroke();

  doc
    .moveTo(COL_DIVIDER_X, HEADER_DIVIDER_Y)
    .lineTo(COL_DIVIDER_X, MARGIN + CONTENT_HEIGHT)
    .lineWidth(1)
    .stroke();
}

function drawHeader(
  doc: any,
  workshopName: string,
  courseLocation: string,
  appointments: AppointmentInfo[],
  logoBuffer?: Buffer | null,
) {
  const logoSpace = logoBuffer ? 90 : 15;

  doc
    .font('Helvetica-Bold')
    .fontSize(15)
    .fillColor(TEXT_PRIMARY)
    .text(workshopName, LEFT_COL_X, MARGIN + 12, {
      width: CONTENT_WIDTH - logoSpace - COL_PADDING,
      lineBreak: true,
      ellipsis: true,
      height: 22,
    });

  const dateStr = getAppointmentDateString(appointments);
  const locationPart = courseLocation ? `${courseLocation}  ` : '';
  doc
    .font('Helvetica')
    .fontSize(10)
    .fillColor(TEXT_SECONDARY)
    .text(`${locationPart}${dateStr}`, LEFT_COL_X, MARGIN + 38, {
      width: CONTENT_WIDTH - logoSpace - COL_PADDING,
    });

  if (logoBuffer) {
    try {
      doc.image(logoBuffer, MARGIN + CONTENT_WIDTH - 80, MARGIN + 8, {
        fit: [65, 50],
        align: 'right',
        valign: 'center',
      });
    } catch {
      // Logo rendering failed, skip silently
    }
  }
}

function drawSteps(
  doc: any,
  steps: Array<{ num: string; title: string; desc: string }>,
  x: number,
  startY: number,
  titleSize = 14,
  descSize = 13,
): number {
  let y = startY;
  for (const step of steps) {
    doc
      .font('Helvetica-Bold')
      .fontSize(titleSize)
      .fillColor(TEXT_PRIMARY)
      .text(`${step.num} ${step.title} `, x, y, { continued: true, width: COL_WIDTH })
      .font('Helvetica')
      .fontSize(descSize)
      .text(step.desc, { width: COL_WIDTH });
    y = doc.y + 10;
  }
  return y;
}

async function createPdfDoc(): Promise<{ doc: any; getBuffer: () => Promise<Buffer> }> {
  const PDFKit = await import('pdfkit');
  const PDFDocument = PDFKit.default;
  const doc = new PDFDocument({ size: 'A4', layout: 'landscape', margin: MARGIN });
  const chunks: Buffer[] = [];
  doc.on('data', (chunk: Buffer) => chunks.push(chunk));
  const getBuffer = () =>
    new Promise<Buffer>((resolve, reject) => {
      doc.on('end', () => resolve(Buffer.concat(chunks)));
      doc.on('error', (err: Error) => reject(err));
      doc.end();
    });
  return { doc, getBuffer };
}

export async function generateTnQrCodePdf(params: TnQrCodePdfParams): Promise<Buffer> {
  const isMulti = params.confirmationLinks.length > 1;

  const qrCodes = new Map<string, string>();
  for (const link of params.confirmationLinks) {
    qrCodes.set(link.appointmentID, await generateQrCodePng(link.url, 250));
  }

  const { doc, getBuffer } = await createPdfDoc();

  for (let i = 0; i < params.confirmationLinks.length; i++) {
    if (i > 0) doc.addPage({ size: 'A4', layout: 'landscape' });
    const link = params.confirmationLinks[i]!;
    const appointment = params.appointments.find(a => a._id === link.appointmentID);

    drawPageFrame(doc, TN_COLOR);
    drawHeader(
      doc,
      params.workshopName,
      params.courseLocation,
      appointment ? [appointment] : params.appointments,
      params.logoBuffer,
    );

    // Left column - instructions
    let y = CONTENT_TOP_Y;

    doc.font('Helvetica-Bold').fontSize(28).fillColor(TN_COLOR);
    doc.text('Teilnahme best\u00e4tigen:', LEFT_COL_X, y);
    y = doc.y + 14;

    doc.font('Helvetica-Bold').fontSize(18).fillColor(TEXT_PRIMARY);
    doc.text('So gehen Sie vor:', LEFT_COL_X, y);
    y += 28;

    y = drawSteps(
      doc,
      [
        {
          num: '1.',
          title: 'QR-Code scannen:',
          desc: 'Kamera \u00f6ffnen und den Code rechts erfassen.',
        },
        {
          num: '2.',
          title: 'Einloggen:',
          desc: 'Melden Sie sich mit dem Account an, mit dem Sie sich zur Veranstaltung angemeldet haben.',
        },
        {
          num: '3.',
          title: 'Erfolg pr\u00fcfen:',
          desc: 'Erscheint das gr\u00fcne H\u00e4kchen? Dann ist Ihre Teilnahme offiziell registriert.',
        },
      ],
      LEFT_COL_X,
      y,
      16,
      15,
    );

    // "Sie kommen nicht weiter?" gray box
    y += 10;
    const fbPadding = 10;
    const fbTitle = 'Sie kommen nicht weiter?';
    const fbBody = 'Akku leer oder der Scan schl\u00e4gt fehl? Kommen Sie bitte zur referierenden Person. Wir tragen Sie manuell ein, damit Ihre Teilnahme dokumentiert ist.';
    const fbTitleH = doc.font('Helvetica-Bold').fontSize(15).heightOfString(fbTitle, { width: COL_WIDTH - 2 * fbPadding });
    const fbBodyH = doc.font('Helvetica').fontSize(13).heightOfString(fbBody, { width: COL_WIDTH - 2 * fbPadding });
    const fbHeight = fbTitleH + fbBodyH + 2 * fbPadding + 8;

    doc.save();
    doc.roundedRect(LEFT_COL_X, y, COL_WIDTH, fbHeight, 3).fillColor('#f2f2f2').fill();
    doc.restore();

    doc.font('Helvetica-Bold').fontSize(15).fillColor(TEXT_PRIMARY);
    doc.text(fbTitle, LEFT_COL_X + fbPadding, y + fbPadding, { width: COL_WIDTH - 2 * fbPadding });
    const fbAfterTitle = doc.y + 8;
    doc.font('Helvetica').fontSize(13).fillColor(TEXT_SECONDARY);
    doc.text(fbBody, LEFT_COL_X + fbPadding, fbAfterTitle, { width: COL_WIDTH - 2 * fbPadding });

    // Right column - QR code
    let ry = CONTENT_TOP_Y;

    const qrLabel = isMulti ? `QR Code Termin ${i + 1}:` : 'QR Code:';
    doc.font('Helvetica-Bold').fontSize(24).fillColor(TN_COLOR);
    doc.text(qrLabel, RIGHT_COL_X, ry, { align: 'center', width: COL_WIDTH });
    ry = doc.y + 10;

    const qrSize = 250;
    const qrX = RIGHT_COL_X + (COL_WIDTH - qrSize) / 2;

    const qrDataUrl = qrCodes.get(link.appointmentID);
    if (qrDataUrl) {
      doc.image(qrDataUrl, qrX, ry, { width: qrSize });
      ry += qrSize + 15;
    }

    doc.font('Helvetica').fontSize(12).fillColor(TEXT_SECONDARY);
    if (link.validFrom) {
      doc.text(`g\u00fcltig ab: ${formatDateTime(link.validFrom)}`, RIGHT_COL_X, ry, { align: 'center', width: COL_WIDTH });
      ry = doc.y + 4;
    }

    if (link.validTill) {
      doc.text(`g\u00fcltig bis: ${formatDateTime(link.validTill)}`, RIGHT_COL_X, ry, { align: 'center', width: COL_WIDTH });
    }
  }

  return getBuffer();
}

export async function generateEvaluationQrCodePdf(params: EvaluationQrCodePdfParams): Promise<Buffer> {
  const separator = params.evaluationLink.endsWith('?') ? '' : '?';
  const fullLink = params.evaluationCode
    ? `${params.evaluationLink}${separator}p=${params.evaluationCode}`
    : params.evaluationLink;
  const qrDataUrl = await generateQrCodePng(fullLink, 250);

  const { doc, getBuffer } = await createPdfDoc();

  drawPageFrame(doc, EVAL_COLOR);
  drawHeader(doc, params.workshopName, params.courseLocation, params.appointments, params.logoBuffer);

  // Left column - instructions
  let y = CONTENT_TOP_Y;

  doc.font('Helvetica-Bold').fontSize(28).fillColor(EVAL_COLOR);
  doc.text('Ihre Meinung z\u00e4hlt!', LEFT_COL_X, y);
  y = doc.y + 14;

  doc.font('Helvetica-Bold').fontSize(18).fillColor(TEXT_PRIMARY);
  doc.text('So gehen Sie vor:', LEFT_COL_X, y);
  y += 28;

  y = drawSteps(
    doc,
    [
      {
        num: '1.',
        title: 'QR-Code scannen:',
        desc: 'Kamera \u00f6ffnen und den Code rechts erfassen.',
      },
      { num: '2.', title: 'Fragen beantworten:', desc: 'Das dauert maximal 5 Minuten.' },
      {
        num: '3.',
        title: 'Absenden:',
        desc: 'Ihr Feedback hilft uns und der referierenden Person, die Workshops stets zu verbessern.',
      },
    ],
    LEFT_COL_X,
    y,
    16,
    15,
  );

  // "Wichtig zu wissen" in gray box — measure content, then anchor to bottom
  const boxPadding = 10;
  const items = [
    { title: 'Anonym:', desc: 'Ihre Antworten lassen keine R\u00fcckschl\u00fcsse auf Ihre Person zu.' },
    { title: 'Ehrlich:', desc: 'Wir sch\u00e4tzen Ihre offene Meinung.' },
    { title: 'Zeit:', desc: 'Wir haben diese Zeit jetzt explizit f\u00fcr Sie eingeplant.' },
  ];

  // Measure height by computing text heights
  const titleHeight = doc.font('Helvetica-Bold').fontSize(15).heightOfString('Wichtig zu wissen:', {
    width: COL_WIDTH - 2 * boxPadding,
  });
  let contentHeight = titleHeight + 10;
  for (let i = 0; i < items.length; i++) {
    const lineText = `- ${items[i]!.title} ${items[i]!.desc}`;
    const lineHeight = doc.font('Helvetica').fontSize(13).heightOfString(lineText, {
      width: COL_WIDTH - 2 * boxPadding,
    });
    contentHeight += lineHeight + (i < items.length - 1 ? 6 : 0);
  }
  const boxHeight = contentHeight + 2 * boxPadding;
  const boxY = MARGIN + CONTENT_HEIGHT - boxHeight - 4;

  doc.save();
  doc.roundedRect(LEFT_COL_X, boxY, COL_WIDTH, boxHeight, 3).fillColor('#f2f2f2').fill();
  doc.restore();

  doc.font('Helvetica-Bold').fontSize(15).fillColor(TEXT_PRIMARY);
  doc.text('Wichtig zu wissen:', LEFT_COL_X + boxPadding, boxY + boxPadding, {
    width: COL_WIDTH - 2 * boxPadding,
  });
  let itemY = doc.y + 10;

  for (let i = 0; i < items.length; i++) {
    const item = items[i]!;
    doc
      .font('Helvetica-Bold')
      .fontSize(13)
      .fillColor(TEXT_PRIMARY)
      .text(`- ${item.title} `, LEFT_COL_X + boxPadding, itemY, {
        continued: true,
        width: COL_WIDTH - 2 * boxPadding,
      })
      .font('Helvetica')
      .text(item.desc, { width: COL_WIDTH - 2 * boxPadding });
    itemY = doc.y + (i < items.length - 1 ? 6 : 0);
  }

  // Right column - QR code
  let ry = CONTENT_TOP_Y;

  doc.font('Helvetica-Bold').fontSize(24).fillColor(EVAL_COLOR);
  doc.text('QR Code f\u00fcr Evaluation:', RIGHT_COL_X, ry, { align: 'center', width: COL_WIDTH });
  ry = doc.y + 10;

  const qrSize = 250;
  const qrX = RIGHT_COL_X + (COL_WIDTH - qrSize) / 2;
  doc.image(qrDataUrl, qrX, ry, { width: qrSize });
  ry += qrSize + 20;

  doc
    .font('Helvetica-Bold')
    .fontSize(12)
    .fillColor(TEXT_PRIMARY)
    .text('QR-Code funktioniert nicht? ', RIGHT_COL_X, ry, { continued: true, width: COL_WIDTH })
    .font('Helvetica')
    .text('Sie k\u00f6nnen URL und Zugangscode auch manuell eingeben:', { width: COL_WIDTH });
  ry = doc.y + 6;

  doc.font('Helvetica').fontSize(11).fillColor(TN_COLOR);
  doc.text(params.evaluationLink, RIGHT_COL_X, ry, { width: COL_WIDTH, underline: true });
  ry = doc.y + 6;

  if (params.evaluationCode) {
    doc.font('Helvetica-Bold').fontSize(12).fillColor(TEXT_PRIMARY);
    doc.text(`Zugangscode: ${params.evaluationCode}`, RIGHT_COL_X, ry, { width: COL_WIDTH });
  }

  return getBuffer();
}
